import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { ReactiveResumeParser } from "@reactive-resume/parser";
import { ResumeData } from "@reactive-resume/schema";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
} from "@reactive-resume/ui";
import { titleFromFileName } from "@reactive-resume/utils";
import { AxiosError } from "axios";
import { Download } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { track } from "@/client/analytics/mixpanel";
import { FancyLoader } from "@/client/components/fancy-loader";
import { useToast } from "@/client/hooks/use-toast";
import { axios } from "@/client/libs/axios";
import { useImportResume } from "@/client/services/resume/import";
import { useAuthStore } from "@/client/stores/auth";
import { useDialog } from "@/client/stores/dialog";

const allowedFileTypes = [
  { name: "PDF", extension: ".pdf" },
  { name: "Word Document", extension: ".doc,.docx" },
  { name: "Text File", extension: ".txt" },
];

// Terms and the AI-processing notice are agreed when the account is created, so the dialog only
// asks for the file.
const formSchema = z.object({
  file: z.instanceof(File),
});

type FormValues = z.infer<typeof formSchema>;

export const ImportDialog = () => {
  const { toast } = useToast();
  const { isOpen, close, payload } = useDialog<{ onImported?: (resume: { id: string }) => void }>(
    "import",
  );
  const { importResume, loading } = useImportResume();
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingImport, setPendingImport] = useState<{
    data: ResumeData;
    parsedEmail: string;
    title?: string;
  } | null>(null);
  const accountEmail = useAuthStore((state) => state.user?.email ?? "");

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });

  // The resume is named after the file it came from.
  const finishImport = async (data: ResumeData, title?: string) => {
    const resume = await importResume({ data, title });
    track("resume_imported");
    toast({ title: t`Resume imported successfully` });
    setPendingImport(null);
    payload?.item?.onImported?.(resume);
    close();
  };

  const processAndImportFile = async (file: File) => {
    setIsProcessing(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `/resume/ai/${file.name.split(".").pop()}resumeformat`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      const parser = new ReactiveResumeParser();
      const validatedData = parser.validate(response.data);
      const convertedData = parser.convert(validatedData as ResumeData);

      const parsedEmail: string = (convertedData as any)?.basics?.email ?? "";

      if (parsedEmail && accountEmail && parsedEmail.toLowerCase() !== accountEmail.toLowerCase()) {
        // Pause and warn — email in file doesn't match the account
        setPendingImport({ data: convertedData, parsedEmail, title: titleFromFileName(file.name) });
      } else {
        await finishImport(convertedData, titleFromFileName(file.name));
      }
    } catch (error) {
      console.error("Error processing file:", error);
      const isTemporarilyUnavailable =
        error instanceof AxiosError &&
        (error.response?.status === 503 || error.code === "ERR_NETWORK");
      toast({
        variant: "error",
        title: isTemporarilyUnavailable
          ? t`The resume parsing service is temporarily unavailable. Please try again.`
          : t`An error occurred while processing the file.`,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const onSubmit = async (values: FormValues) => {
    await processAndImportFile(values.file);
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <DialogHeader>
              <DialogTitle>
                <div className="flex items-center space-x-2.5">
                  <Download className="size-5" aria-hidden="true" />
                  <h2>{t`Import your resume`}</h2>
                </div>
              </DialogTitle>
              <DialogDescription>
                <div className="flex justify-center">
                  <img
                    className="jd-done"
                    width={250}
                    height={250}
                    alt="image"
                    src="/backgrounds/importFile.svg"
                  />
                </div>
                <div>{t`Upload a file to complete your profile.`}</div>
              </DialogDescription>
            </DialogHeader>

            <FormField
              name="file"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="space-y-2">
                      <Input
                        type="file"
                        className="leading-10"
                        accept={allowedFileTypes.map((type) => type.extension).join(",")}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            field.onChange(file);
                          }
                        }}
                      />
                      <p className="text-sm text-gray-500">
                        {t`Allowed file types: ${allowedFileTypes
                          .map((type) => type.name)
                          .join(", ")}`}
                      </p>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isProcessing && (
              <div className="flex justify-center">
                <FancyLoader />
              </div>
            )}

            {pendingImport && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                <p className="font-medium">{t`This doesn't look like your resume`}</p>
                <p className="mt-1">
                  {t`The uploaded file contains the email `}
                  <span className="font-mono font-semibold">{pendingImport.parsedEmail}</span>
                  {t`, which is different from your account email `}
                  <span className="font-mono font-semibold">{accountEmail}</span>
                  {t`. Your account email will be used on the resume — recruiters will contact you there.`}
                </p>
                <div className="mt-3 flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => finishImport(pendingImport.data, pendingImport.title)}
                    disabled={loading}
                  >
                    {t`Yes, import anyway`}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setPendingImport(null)}
                  >
                    {t`Cancel`}
                  </Button>
                </div>
              </div>
            )}

            {!pendingImport && (
              <Button type="submit" className="w-full" disabled={isProcessing || loading}>
                {isProcessing ? t`Processing...` : t`Import Resume`}
              </Button>
            )}
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
